import algoliasearch from 'algoliasearch/dist/algoliasearch-lite.esm.browser'
import type { SearchClient } from 'algoliasearch/lite'
import type { FC } from 'react'
import React, { useState } from 'react'
import ReactDOM from 'react-dom'
import { Configure, InstantSearch } from 'react-instantsearch-core'
import { z } from 'zod'

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { App } from './layouts/inspiration'

const Settings = z.object({
  handle: z.string().min(1),
  collectionName: z.string().min(1),
  algolia: z.object({
    APPLICATION_ID: z.string().min(1),
    SEARCH_API_KEY: z.string().min(1),
    INDEX_NAME: z.string().min(1),
    HITS_PER_PAGE: z.number()
  })
})

type Settings = z.infer<typeof Settings>

export const Application: FC = () => {
  const client = new QueryClient()

  const [settings] = useState<Settings | null>(function () {
    const dataItem = document.getElementById('algolia-settings-json')

    if (dataItem == null) {
      console.error(new Error('Algolia setting element is missing'))
      return null
    }

    return Settings.parse(JSON.parse(dataItem.innerText))
  })

  const [searchClient] = useState<SearchClient | null>(function () {
    if (settings == null) {
      return null
    }

    return algoliasearch(
      settings.algolia.APPLICATION_ID,
      settings.algolia.SEARCH_API_KEY
    )
  })

  if (settings == null || searchClient == null) {
    return null
  }

  const { handle } = settings

  return (
    <InstantSearch
      searchClient={searchClient}
      indexName={settings.algolia.INDEX_NAME}
      routing={false}
    >
      {handle && (
        <Configure
          distinct={false} // Disable default deduplication by Title to show all inspiration products, see RR-457
          hitsPerPage={settings.algolia.HITS_PER_PAGE}
          clickAnalytics={true}
          filters={`collections:${handle}`}
          ruleContexts={[handle]}
          getRankingInfo={true}
        />
      )}

      {!handle && (
        <Configure
          distinct={true}
          hitsPerPage={settings.algolia.HITS_PER_PAGE}
          clickAnalytics={true}
          filters={`inventory_available:true`}
        />
      )}

      <QueryClientProvider {...{ client }}>
        <App hits_per_page={settings.algolia.HITS_PER_PAGE} />
      </QueryClientProvider>
    </InstantSearch>
  )
}

const element = document.querySelector('#algolia-container')

ReactDOM.render(<Application />, element)
